import React, { useState } from 'react';
import { BoltIcon } from '@heroicons/react/24/outline';

interface GetStartedTooltipProps {
  isMember: boolean;
  onGetStarted?: () => void;
  position?: 'bottom-right' | 'top-right';
}

const GetStartedTooltip: React.FC<GetStartedTooltipProps> = ({ 
  isMember, 
  onGetStarted,
  position = 'bottom-right'
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (isMember) {
    return null; // Don't show if user is already a member
  }

  const handleClick = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      // Link to app with KYC modal opened automatically (same as "Get Member ID" button)
      window.open('https://app.bornfree.io/?kyc=true', '_blank');
    }
  };

  const positionClasses = position === 'top-right' 
    ? 'fixed top-24 right-6' 
    : 'fixed bottom-6 right-6';

  return (
    <div className={`${positionClasses} z-50`}>
      {/* Glowing Info Icon */}
      <button
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="relative group"
        aria-label="Start Here"
        style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px' }}
      >
        {/* Green/White Alternating Glow effect - Centered */}
        <div style={{
          position: 'absolute',
          width: '40px',
          height: '40px',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          backgroundColor: 'rgba(34, 197, 94, 0.6)',
          filter: 'blur(10px)',
          animation: 'alternatingGlow 2s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          width: '40px',
          height: '40px',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          filter: 'blur(8px)',
          animation: 'alternatingGlowReverse 2s ease-in-out infinite'
        }}></div>
        
        {/* Power Button ON Icon - Green on hover, White otherwise */}
        <svg
          style={{ 
            position: 'relative',
            width: '28px', 
            height: '28px', 
            color: showTooltip ? '#22c55e' : '#ffffff',
            filter: showTooltip ? 'drop-shadow(0 0 6px rgba(34, 197, 94, 0.8))' : 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.8))',
            transition: 'all 0.3s ease',
            zIndex: 1
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.15)';
            e.currentTarget.style.color = '#22c55e';
            e.currentTarget.style.filter = 'drop-shadow(0 0 6px rgba(34, 197, 94, 0.8))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.filter = 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.8))';
          }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 2v10M18 6a9 9 0 1 1-12 0" />
        </svg>

        {/* "Start" text on hover only - Yellow on hover */}
        {showTooltip && (
          <div className="absolute top-full right-0 mt-2 whitespace-nowrap" style={{ paddingBottom: '4px' }}>
            <span 
              className="font-bold text-sm transition-colors duration-300" 
              style={{ 
                backgroundColor: 'transparent',
                color: '#eab308',
                display: 'block'
              }}
            >
              Start
            </span>
          </div>
        )}
      </button>

      <style>{`
        @keyframes alternatingGlow {
          0%, 100% {
            opacity: 0.6;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.2;
            transform: translate(-50%, -50%) scale(1.2);
          }
        }
        @keyframes alternatingGlowReverse {
          0%, 100% {
            opacity: 0.2;
            transform: translate(-50%, -50%) scale(1.2);
          }
          50% {
            opacity: 0.6;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default GetStartedTooltip;
